/** @jsx jsx */
import { useContext } from "react"
import { jsx, useThemeUI } from "theme-ui"
import PropTypes from "prop-types"

import ModeContext from "../context/mode-context"
import Link from "./link"
import Arrow from "./arrow"
import { ab } from "../theme"

const SectionIndex = ({ entries = [], cta, mode, heading }) => {
    const { mode: globalMode } = useContext(ModeContext)
    const { rawColors: colors } = useThemeUI().theme

    const modeKey = mode ? mode : globalMode
    const modeColor = colors.modes[modeKey]?.primary ?? colors.modes["group"].primary

    const isSolid = cta.buttonVariant === "solid"

    const buttonSx = isSolid
        ? {
            variant: "styles.solidButton",
            px: 4,
            py: 2,
            bg: modeColor,
            borderColor: modeColor,
            color: "#fff",
            "&:hover": {
                bg: modeColor,
                color: "#fff",
            },
        }
        : {
            variant: "styles.ghostButton",
            px: 4,
            py: 2,
            color: modeColor,
            borderColor: modeColor,
            "&:hover": {
                bg: modeColor,
                color: "#fff",
            },
        }
    
    const hasButton = 
    cta.buttonLink && 
    cta.buttonText

    return (
        <section
        sx = {{
            variant: "spacingContainer",
        }}>
            {heading &&<h3 sx={{ mb: 2 }}>{heading}</h3>}
            <div 
            sx = {{ 
                display: "flex",
                flexDirection: ab({ _: "column", md: "row" }),
                alignItems: ab({md: "flex-start" }),
                gap: ab({ _: 4, md: 5 }),
                }}>
                <ul 
                sx = {{
                    flex: ab({ md: 1}),
                    listStyle: "none", 
                    p: 0,
                    m: 0
                }}>
                    {entries.map((entry, i) => (
                        <li 
                            key = {i} 
                            sx = {{ 
                                borderBottom: "1px solid #ccc",
                                py: 2
                            }}
                        >
                            <a 
                                href = {`#${entry.anchor}`}
                                sx = {{ 
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 2,
                                    color: "primary",
                                    textDecoration: "none",
                                }}
                            >
                            <span sx={{
                                flexShrink: 0,
                                width: "30px",
                                height: "30px",
                                display: "inline-flex",
                                transform: "scaleX(-1)",
                                svg: { width: "100%", height: "100%", display: "block" },
                            }}>
                                <Arrow color={modeColor} />
                            </span>
                            {entry.label}
                            </a>
                        </li>
                    ))}
                </ul>
                    
                    <div 
                    sx = {{
                        flex: ab({ md: 1}),
                        display: "flex",
                        flexDirection: "column",
                        gap: 3,
                    }}>
                        {cta.image &&
                            <img
                                src={cta.image}
                                alt={cta.imageAlt}
                                sx = {{
                                    width: "100%",
                                    height: "auto",
                                display: "block"
                            }}
                        />}
                        <div 
                        sx = {{
                            display: "flex",
                            flexDirection: "column",
                            gap: 2,
                            px: 0,
                            pb: 3
                        }}>
                            {cta.headline && (
                                <h3 sx = {{ m: 0 }}>
                                    {cta.headline}
                                </h3>
                            )}
                            {cta.description && (
                                <p sx = {{ m: 0 }}>
                                    {cta.description}
                                </p>
                            )}
                            {hasButton && (
                                <Link
                                    to={cta.buttonLink}
                                    sx = {{
                                        ...buttonSx,
                                        alignSelf: "flex-start",
                                        mt: 3,

                                    }}
                                >
                                    {cta.buttonText}
                                </Link>
                            )}
                        </div>
                    </div>
                
            </div>
        </section>
    )
}

SectionIndex.propTypes = {
    heading: PropTypes.string,
    entries: PropTypes.arrayOf(
        PropTypes.shape({
        label: PropTypes.string,
        anchor: PropTypes.string,
        }).isRequired
    ),
    cta: PropTypes.shape({
        headline: PropTypes.string,
        description: PropTypes.string,
        image: PropTypes.string,
        imageAlt: PropTypes.string,
        buttonLink: PropTypes.string,
        buttonText: PropTypes.string,
        buttonVariant: PropTypes.oneOf([
            "ghost",
            "solid",
        ]),
    }),
    mode: PropTypes.string,
}

export default SectionIndex
